import { useState } from 'react';

import { useBulkCreateRights, useCreateRight, useRights, useShows } from '../../api/hooks';
import type { RightView } from '../../api/types';
import {
  Badge,
  Button,
  DataTable,
  FormField,
  Modal,
  Select,
  useToast,
} from '../../components/DesignSystem';
import type { DataTableColumn } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { formatCurrency, formatDate, formatPercent } from '../../lib/format';
import { draftToInput, EMPTY_RIGHT_DRAFT, RightForm } from './RightForm';
import type { RightDraft } from './RightForm';

const RIGHT_COLUMNS: DataTableColumn<RightView>[] = [
  {
    key: 'show',
    header: 'Show / Season',
    render: (row) => `${row.showName}${row.seasonCode ? ` · ${row.seasonCode}` : ''}`,
  },
  { key: 'trackId', header: 'Track ID' },
  { key: 'partyName', header: 'Party' },
  {
    key: 'rightType',
    header: 'Type',
    render: (row) => (
      <Badge tone={row.rightType === 'primary' ? 'primary' : 'teal'}>{row.rightType}</Badge>
    ),
  },
  { key: 'category', header: 'Category' },
  { key: 'mediaType', header: 'Media Type' },
  { key: 'territory', header: 'Territory' },
  {
    key: 'sharePercent',
    header: 'Share %',
    align: 'right',
    render: (row) => formatPercent(row.sharePercent),
  },
  { key: 'licenseStart', header: 'License Start', render: (row) => formatDate(row.licenseStart) },
  { key: 'licenseEnd', header: 'License End', render: (row) => formatDate(row.licenseEnd) },
  {
    key: 'licenseFee',
    header: 'License Fee',
    align: 'right',
    render: (row) => formatCurrency(row.licenseFee),
  },
  {
    key: 'feePerEpisode',
    header: 'Fee/Episode',
    align: 'right',
    render: (row) => formatCurrency(row.feePerEpisode),
  },
  {
    key: 'isExclusive',
    header: 'Exclusive',
    align: 'center',
    render: (row) => (row.isExclusive ? 'Yes' : '—'),
  },
  {
    key: 'isHoldback',
    header: 'Holdback',
    align: 'center',
    render: (row) => (row.isHoldback ? 'Yes' : '—'),
  },
];

type ModalKind = 'none' | 'add' | 'bulk';

/** Manage Rights: filterable table with single and bulk add modals. */
export function ManageRightsTab() {
  const { showToast } = useToast();
  const shows = useShows({ limit: 100 });

  const [showId, setShowId] = useState('');
  const [seasonId, setSeasonId] = useState('');
  const [modal, setModal] = useState<ModalKind>('none');
  const [draft, setDraft] = useState<RightDraft>(EMPTY_RIGHT_DRAFT);
  const [bulkDrafts, setBulkDrafts] = useState<RightDraft[]>([EMPTY_RIGHT_DRAFT]);

  const rights = useRights({
    limit: 100,
    ...(showId !== '' ? { showId } : {}),
    ...(seasonId !== '' ? { seasonId } : {}),
  });

  const createRight = useCreateRight();
  const bulkCreate = useBulkCreateRights();

  const showList = shows.data?.data ?? [];
  const selectedShow = showList.find((show) => show.id === showId);

  async function handleAdd() {
    const input = draftToInput(draft);

    if (input === null) {
      showToast('Show, media type, and territory are required', 'error');
      return;
    }

    try {
      await createRight.mutateAsync(input);
      showToast('Right added');
      setDraft(EMPTY_RIGHT_DRAFT);
      setModal('none');
    } catch (error) {
      showToast(errorMessage(error, 'Could not add right'), 'error');
    }
  }

  async function handleBulkAdd() {
    const inputs = bulkDrafts.map(draftToInput);

    if (inputs.some((input) => input === null) || inputs.length === 0) {
      showToast('Every row needs a show, media type, and territory', 'error');
      return;
    }

    try {
      const result = await bulkCreate.mutateAsync(
        inputs.filter((input): input is NonNullable<typeof input> => input !== null),
      );
      showToast(`${result.count} rights created`);
      setBulkDrafts([EMPTY_RIGHT_DRAFT]);
      setModal('none');
    } catch (error) {
      showToast(errorMessage(error, 'Bulk add failed'), 'error');
    }
  }

  return (
    <>
      <div className="rfp-toolbar">
        <div className="rfp-row">
          <FormField htmlFor="rights-filter-show" label="Show">
            <Select
              id="rights-filter-show"
              onChange={(event) => {
                setShowId(event.target.value);
                setSeasonId('');
              }}
              value={showId}
            >
              <option value="">All shows</option>
              {showList.map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField htmlFor="rights-filter-season" label="Season">
            <Select
              id="rights-filter-season"
              onChange={(event) => setSeasonId(event.target.value)}
              value={seasonId}
            >
              <option value="">All seasons</option>
              {(selectedShow?.seasons ?? []).map((season) => (
                <option key={season.id} value={season.id}>
                  {season.seasonCode}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
        <div className="rfp-page-head__actions">
          <Button onClick={() => setModal('bulk')} type="button" variant="secondary">
            Bulk Add
          </Button>
          <Button onClick={() => setModal('add')} type="button">
            Add Right
          </Button>
        </div>
      </div>

      <section className="rfp-card">
        {rights.isLoading ? (
          <p className="rfp-loading">Loading rights…</p>
        ) : (
          <div className="rfp-table-scroll">
            <DataTable
              columns={RIGHT_COLUMNS}
              emptyMessage="No rights yet — add one to start tracking availability"
              rowKey={(row) => row.id}
              rows={rights.data?.data ?? []}
            />
          </div>
        )}
      </section>

      {modal === 'add' && (
        <Modal
          actions={
            <>
              <Button disabled={createRight.isPending} onClick={handleAdd} type="button">
                {createRight.isPending ? 'Adding…' : 'Add Right'}
              </Button>
              <Button onClick={() => setModal('none')} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title="Add Right"
        >
          <div className="rfp-modal-wide">
            <RightForm draft={draft} idPrefix="add-right" onChange={setDraft} />
          </div>
        </Modal>
      )}

      {modal === 'bulk' && (
        <Modal
          actions={
            <>
              <Button
                onClick={() => setBulkDrafts((current) => [...current, EMPTY_RIGHT_DRAFT])}
                type="button"
                variant="secondary"
              >
                + Add another row
              </Button>
              <Button disabled={bulkCreate.isPending} onClick={handleBulkAdd} type="button">
                {bulkCreate.isPending ? 'Creating…' : `Create ${bulkDrafts.length} right(s)`}
              </Button>
              <Button onClick={() => setModal('none')} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title="Bulk Add Rights"
        >
          <div className="rfp-modal-wide rfp-stack">
            {bulkDrafts.map((rowDraft, index) => (
              <div key={index}>
                <p className="rfp-step-label">— Right {index + 1}</p>
                <RightForm
                  draft={rowDraft}
                  idPrefix={`bulk-right-${index}`}
                  onChange={(next) =>
                    setBulkDrafts((current) =>
                      current.map((entry, entryIndex) => (entryIndex === index ? next : entry)),
                    )
                  }
                />
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}
